import React, { useState } from "react";
import { Sparkles, MapPin, Star, X, CheckCircle2, Loader2, Building2, Globe } from "lucide-react";
import { tripSuggestionsApi, apiErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { ProspectDrawer } from "@/components/ProspectDrawer";

const CHIP = "inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold";

export function TripSuggestionPanel({ visitId, onApproved }) {
  const [sug, setSug] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [drawer, setDrawer] = useState(null);

  const run = async (fn, msg) => {
    setLoading(true);
    try {
      const r = await fn();
      setSug(r.data);
      if (msg) toast.success(msg);
      return r.data;
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setLoading(false); }
  };

  const compute = () => run(() => tripSuggestionsApi.compute(visitId), "Nearby prospects computed");
  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const remove = (cid) => run(() => tripSuggestionsApi.removeCandidate(sug.id, cid));
  const approve = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    const r = await tripSuggestionsApi.approve(sug.id, { selected_candidate_ids: ids }).catch((e) => toast.error(apiErrorMessage(e)));
    if (r) { toast.success(`${r.data.visits_created} visit(s) added to the trip`); onApproved?.(); run(() => tripSuggestionsApi.get(visitId)); }
  };

  const cands = sug?.candidates || [];
  const chosenCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="mt-2 rounded-xl border border-teal-200 bg-teal-50/40 p-3" data-testid="trip-suggestion-panel">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs font-bold text-teal-800"><Sparkles className="h-4 w-4" /> Make this trip worthwhile</span>
        <button data-testid="trip-compute-btn" onClick={compute} disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-lg bg-teal-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-teal-800 disabled:opacity-50">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Star className="h-3.5 w-3.5" />}
          {sug && sug.status === "active" ? "Refresh" : "Find prospects"}
        </button>
      </div>

      {sug && sug.status === "active" && (
        <>
          <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-500" data-testid="trip-capacity">
            <span className={`${CHIP} border-slate-200 bg-white`}>Radius {sug.radius_km}km{sug.expanded ? " (expanded)" : ""}</span>
            <span className={`${CHIP} border-slate-200 bg-white`}>Capacity {sug.capacity.used_min}/{sug.capacity.available_min} min</span>
            <span className={`${CHIP} border-slate-200 bg-white`}>{sug.capacity.days}-day</span>
            {sug.discovery_used && <span className={`${CHIP} border-amber-200 bg-amber-50 text-amber-700`}>FSA discovery used</span>}
          </div>
          {cands.length === 0 ? (
            <p className="mt-3 text-xs text-slate-400" data-testid="trip-empty">No worthwhile prospects within range for this visit.</p>
          ) : (
            <div className="mt-2 space-y-2">
              {cands.map((c) => (
                <div key={c.id} data-testid={`trip-candidate-${c.id}`}
                  className={`rounded-lg border p-2.5 ${selected[c.id] ? "border-teal-400 bg-white" : "border-slate-200 bg-white"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-start gap-1">
                      <label data-selection-control htmlFor={`trip-select-${c.id}`}
                        onClick={(e) => e.stopPropagation()} style={{ touchAction: "manipulation" }}
                        className="relative -ml-1 flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-md focus-within:ring-2 focus-within:ring-teal-500">
                        <input id={`trip-select-${c.id}`} type="checkbox" data-testid={`trip-select-${c.id}`}
                          checked={!!selected[c.id]}
                          onChange={(e) => { e.stopPropagation(); toggle(c.id); }}
                          onClick={(e) => e.stopPropagation()}
                          className="h-5 w-5 accent-teal-700" />
                      </label>
                      <span className="min-w-0 pt-2 cursor-pointer" data-testid={`trip-card-body-${c.id}`}
                        onClick={() => c.is_external && c.external_id && setDrawer(c)}>
                        <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                          {c.is_external ? <Globe className="h-3.5 w-3.5 text-amber-600" /> : <Building2 className="h-3.5 w-3.5 text-teal-600" />}
                          {c.name || "Prospect"}
                        </span>
                        <span className="mt-1 flex flex-wrap gap-1">
                          {c.fhrs_rating && <span className={`${CHIP} border-emerald-200 bg-emerald-50 text-emerald-700`}>FHRS {c.fhrs_rating}</span>}
                          {c.best_fit && <span className={`${CHIP} border-teal-200 bg-teal-50 text-teal-700`}>{c.best_fit}</span>}
                          <span className={`${CHIP} border-slate-200 bg-slate-50 text-slate-600`}>{c.distance_km}km · {c.estimated_travel_min}min</span>
                          <span className={`${CHIP} border-indigo-200 bg-indigo-50 text-indigo-700`}>{c.channel}</span>
                          <span className={`${CHIP} ${c.is_external ? "border-amber-200 bg-amber-50 text-amber-700" : "border-slate-200 bg-slate-50 text-slate-500"}`}>{c.is_external ? "external" : "CRM lead"}</span>
                        </span>
                        {(c.reasons || []).slice(0, 3).map((r, i) => (
                          <span key={i} className="mt-1 block text-[11px] text-slate-500">• {r}</span>
                        ))}
                      </span>
                    </div>
                    <button data-testid={`trip-remove-${c.id}`} onClick={() => remove(c.id)} title="Remove (7 days)"
                      className="shrink-0 rounded p-1 text-slate-300 hover:text-rose-500"><X className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
              <button data-testid="trip-approve-btn" onClick={approve} disabled={chosenCount === 0}
                className="inline-flex min-h-[40px] w-full items-center justify-center gap-2 rounded-lg bg-teal-700 px-3 text-sm font-bold text-white hover:bg-teal-800 disabled:opacity-40">
                <CheckCircle2 className="h-4 w-4" /> Approve {chosenCount} selected
              </button>
            </div>
          )}
        </>
      )}
      <ProspectDrawer
        prospect={drawer}
        onClose={() => setDrawer(null)}
        selected={drawer ? !!selected[drawer.id] : false}
        onToggleSelect={() => drawer && toggle(drawer.id)}
        onConverted={() => { setDrawer(null); if (sug) run(() => tripSuggestionsApi.get(visitId)); }}
        onDismiss={() => { if (drawer) { remove(drawer.id); setDrawer(null); } }}
      />
    </div>
  );
}
